import { ReactNode, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useOnboarding } from '@/hooks/useOnboarding';
import { useRestaurant } from '@/contexts/RestaurantContext';
import { useUserRole } from '@/hooks/useUserRole';

interface OnboardingGuardProps {
  children: ReactNode;
}

export const OnboardingGuard = ({ children }: OnboardingGuardProps) => {
  const { user } = useAuth();
  const { isLoading: restaurantLoading, currentRestaurant } = useRestaurant();
  const { isMasterAdmin } = useUserRole();
  const { onboardingData, isLoading: onboardingLoading } = useOnboarding(user?.id);
  const navigate = useNavigate();
  const location = useLocation();

  // Aguardar AMBOS os loadings para evitar redirecionamento prematuro
  const isLoading = onboardingLoading || restaurantLoading;

  const shouldRedirect = useMemo(() => {
    if (isLoading || !onboardingData || !user) return null;

    // NEW: Unit Managers (invited users) bypass onboarding entirely
    // They don't own a restaurant and shouldn't go through setup - restaurant is already configured by owner
    if (!isMasterAdmin && currentRestaurant) {
      return null; // Allow access without onboarding checks
    }

    // NEW: If Master Admin is viewing a non-headquarters unit, bypass onboarding checks
    // Secondary units inherit configuration from headquarters
    if (isMasterAdmin && currentRestaurant && !currentRestaurant.is_headquarters) {
      return null; // Allow navigation without onboarding checks
    }

    const hasServiceType = !!onboardingData.selected_service_type;
    const isOnWizardPage = location.pathname === '/onboarding-wizard';
    const isAuthPage = location.pathname === '/auth';

    // Se não selecionou tipo de serviço, ir para wizard
    if (!hasServiceType && !isOnWizardPage && !isAuthPage) {
      return '/onboarding-wizard';
    }

    // Se já selecionou tipo e está no wizard, redirecionar para dashboard
    if (hasServiceType && isOnWizardPage && onboardingData.onboarding_completed) {
      return '/dashboard';
    }

    return null;
  }, [isLoading, onboardingData, user, location.pathname, isMasterAdmin, currentRestaurant]);

  useEffect(() => {
    if (shouldRedirect) {
      navigate(shouldRedirect, { replace: true });
    }
  }, [shouldRedirect, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return <>{children}</>;
};
